import { ChevronDown } from 'lucide-react';
import { FC, useEffect, useRef, useState } from 'react';
import styles from './PunchupFilterSelect.module.css';

export interface PunchupFilterOption {
	label: string;
	value: string;
	/** When true, renders the option with a red background to signal a dangerous operation. */
	danger?: boolean;
}

interface PunchupFilterSelectProps {
	options: Array<PunchupFilterOption>;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	/** Additional CSS class applied to the outer wrapper — use to override sizing. */
	className?: string;
	/** Accessible name for the trigger button. Use when the dropdown has no visible label. */
	ariaLabel?: string;
	/** Which trigger edge the dropdown panel anchors to. `'right'` opens it leftward — use when the
	 *  trigger sits near the right edge of its container so the panel doesn't overflow off-screen.
	 *  Defaults to `'left'` (opens rightward). */
	menuAlign?: 'left' | 'right';
}

const PunchupFilterSelect: FC<PunchupFilterSelectProps> = ({
	options,
	value,
	onChange,
	placeholder,
	className,
	ariaLabel,
	menuAlign = 'left',
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	const selectedOption = options.find((o) => o.value === value);
	const displayLabel = selectedOption?.label ?? placeholder ?? '';

	useEffect(() => {
		const handleOutside = (e: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleOutside);
		return () => document.removeEventListener('mousedown', handleOutside);
	}, []);

	useEffect(() => {
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setIsOpen(false);
		};
		document.addEventListener('keydown', handleKey);
		return () => document.removeEventListener('keydown', handleKey);
	}, []);

	const handleSelect = (optValue: string) => {
		onChange(optValue);
		setIsOpen(false);
	};

	return (
		<div ref={wrapperRef} className={`${styles.wrapper}${className ? ` ${className}` : ''}`}>
			<button
				type="button"
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				// Below 768px the visible label collapses out (chevron-only); the trigger's
				// accessible name falls back to the displayed label so screen-reader users
				// still hear the active filter value when there is no explicit `ariaLabel`.
				aria-label={ariaLabel ?? displayLabel}
				className={`${styles.trigger}${isOpen ? ` ${styles.triggerOpen}` : ''}`}
				onClick={() => setIsOpen((prev) => !prev)}
			>
				<span className={styles.triggerLabel}>{displayLabel}</span>
				<ChevronDown size={16} className={`${styles.chevron}${isOpen ? ` ${styles.chevronOpen}` : ''}`} />
			</button>

			{isOpen && (
				<div role="listbox" className={`${styles.panel}${menuAlign === 'right' ? ` ${styles.panelRight}` : ''}`}>
					{options.map((option) => {
						const isSelected = option.value === value;
						return (
							<button
								key={option.value}
								type="button"
								role="option"
								aria-selected={isSelected}
								className={[
									styles.option,
									option.danger ? styles.optionDanger : '',
									isSelected ? styles.optionSelected : '',
								]
									.filter(Boolean)
									.join(' ')}
								onClick={() => handleSelect(option.value)}
							>
								{option.label}
								{isSelected && (
									<span className={styles.optionCheckBox} aria-hidden="true">
										<svg className={styles.optionCheckSvg} viewBox="0 0 10 8" fill="none" aria-hidden="true">
											<path
												d="M1 4L3.5 6.5L9 1"
												stroke="currentColor"
												strokeWidth="1.75"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</span>
								)}
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
};

export { PunchupFilterSelect };
